import { AlertTriangle, ExternalLink, EyeOff, Home, MapPin } from 'lucide-react'

import type { Parcel } from '../api/types'
import { de } from '../i18n/de'
import { dayLabel, formatDeliveryWindow, formatMoment, parseDate } from '../lib/format'
import { isOrderKeyed, overdueDays, parcelTitle, siteLabel, staleDays } from '../lib/parcel'
import { CarrierBadge } from './CarrierBadge'
import { DescriptionChip } from './DescriptionChip'
import { FavoriteStar } from './FavoriteStar'
import { ReturnBadge } from './ParcelDrawer'
import { ProjectChip } from './ProjectChip'
import { StatusRoute } from './StatusRoute'
import { TrackingNumber } from './TrackingNumber'

/**
 * One row, used identically in every section.
 *
 * Two lines: what it is (always a title, the sender standing in when the content is not
 * known yet), then who carries it, where it goes and its number. On a wide screen status
 * and date flank the row; on a phone they share a last line, so the title keeps the width.
 */
export function ParcelRow({
  parcel,
  onOpen,
  dimmed = false,
  mailOnly = false,
  inOverdueGroup = false,
}: {
  parcel: Parcel
  onOpen: (id: number) => void
  dimmed?: boolean
  /** This parcel's carrier has no API, so only a mail can ever move it. */
  mailOnly?: boolean
  /** Listed under "Überfällig", so the row need not say it again. */
  inOverdueGroup?: boolean
}) {
  const orderKeyed = isOrderKeyed(parcel)
  const title = parcelTitle(parcel)
  const site = siteLabel(parcel)

  return (
    <li
      onClick={() => onOpen(parcel.id)}
      className={`group flex cursor-pointer items-start gap-3 border-b border-slate-200 px-4 py-3 transition-colors last:border-0 hover:bg-slate-50 sm:items-center dark:border-slate-800 dark:hover:bg-slate-800/40 ${
        dimmed ? 'opacity-75' : ''
      }`}
    >
      <div className="hidden w-[8.5rem] shrink-0 items-center gap-1 sm:flex">
        <FavoriteStar parcelId={parcel.id} favorite={parcel.favorite} />
        <StatusRoute status={parcel.status} />
      </div>
      <div className="-ml-1 sm:hidden">
        <FavoriteStar parcelId={parcel.id} favorite={parcel.favorite} />
      </div>

      <div className="min-w-0 flex-1">
        <div className="flex items-start gap-2">
          <div className="flex min-w-0 flex-1 flex-wrap items-center gap-x-2 gap-y-1">
            <span
              className={`min-w-0 truncate ${
                title.fallback
                  ? 'text-slate-500 dark:text-slate-400'
                  : 'font-medium text-slate-900 dark:text-slate-100'
              }`}
              title={title.text}
            >
              {title.text}
            </span>
            {title.fallback && (
              <DescriptionChip parcelId={parcel.id} description={parcel.description} />
            )}
            <ProjectChip
              parcelId={parcel.id}
              projectId={parcel.project_id}
              projectName={parcel.project_name}
            />
            {parcel.is_return && <ReturnBadge />}
            {parcel.hidden_at && (
              <span
                title={
                  parcel.hidden_by ? de.parcel.hiddenBy(parcel.hidden_by) : de.parcel.hiddenBadge
                }
                className="inline-flex shrink-0 items-center gap-1 rounded-full bg-amber-100 px-1.5 py-0.5 text-xs text-amber-800 dark:bg-amber-950 dark:text-amber-200"
              >
                <EyeOff className="h-3 w-3" aria-hidden />
                {de.parcel.hidden}
              </span>
            )}
          </div>
        </div>

        <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-[13px] text-slate-500 dark:text-slate-400">
          <CarrierBadge
            code={parcel.carrier_code}
            name={parcel.carrier_name}
            confidence={parcel.carrier_confidence}
          />
          {site && (
            <span
              className="inline-flex items-center gap-1 font-medium text-slate-700 dark:text-slate-300"
              title={
                parcel.recipient_address_partial
                  ? de.parcel.addressFromCarrierOnlyCity
                  : (parcel.recipient_address ?? undefined)
              }
            >
              <Home className="h-3 w-3 shrink-0" aria-hidden />
              {site}
            </span>
          )}
          {parcel.pickup_location && (
            <span className="inline-flex min-w-0 items-center gap-1 truncate font-medium text-sky-700 dark:text-sky-300">
              <MapPin className="h-3 w-3 shrink-0" aria-hidden />
              <span className="truncate">{parcel.pickup_location}</span>
            </span>
          )}
          <TrackingNumber
            value={orderKeyed ? parcel.order_number : parcel.tracking_number}
            display={orderKeyed ? parcel.order_number : undefined}
            label={orderKeyed ? de.parcel.orderNumber : de.parcel.trackingNumber}
          />
          {/* Already the title when there is no description; then it would say it twice. */}
          {!title.fallback && parcel.sender_name && (
            <span className="truncate">{parcel.sender_name}</span>
          )}
          {parcel.recipient_name && <span className="truncate">→ {parcel.recipient_name}</span>}
          <span className="mt-1 flex basis-full items-end justify-between gap-3 sm:hidden">
            <StatusRoute status={parcel.status} />
            <span className="text-right">
              <ParcelDate parcel={parcel} mailOnly={mailOnly} short={inOverdueGroup} />
            </span>
          </span>
        </div>
      </div>

      <div className="hidden shrink-0 items-center gap-2 text-right sm:flex">
        <ParcelDate parcel={parcel} mailOnly={mailOnly} short={inOverdueGroup} />
        {parcel.tracking_url ? (
          <a
            href={parcel.tracking_url}
            target="_blank"
            rel="noreferrer noopener"
            onClick={(event) => event.stopPropagation()}
            title={orderKeyed ? de.parcel.openAtAmazon : de.parcel.openAtCarrier}
            className="rounded p-1 text-slate-400 opacity-0 transition group-hover:opacity-100 hover:bg-slate-200 hover:text-slate-700 focus:opacity-100 dark:hover:bg-slate-700 dark:hover:text-slate-200"
          >
            <ExternalLink className="h-4 w-4" aria-hidden />
          </a>
        ) : (
          <span className="w-6" aria-hidden />
        )}
      </div>
    </li>
  )
}

/**
 * When: the delivery day once delivered, otherwise the expected window, otherwise how fresh
 * the data is. The day itself carries the weight; the label in front stays quiet.
 *
 * Below it at most one warning: overdue when the expected window has passed, otherwise the
 * silence of a mail-only parcel. Both say "look at this"; one line is enough.
 */
function ParcelDate({
  parcel,
  mailOnly,
  short,
}: {
  parcel: Parcel
  mailOnly: boolean
  short: boolean
}) {
  const delivered = parseDate(parcel.delivered_at)
  const hasWindow = Boolean(parcel.expected_delivery_from || parcel.expected_delivery_to)
  const overdue = overdueDays(parcel)
  const stale = staleDays(parcel, mailOnly)

  return (
    <div className="text-xs whitespace-nowrap text-slate-500 dark:text-slate-400">
      {parcel.status === 'DELIVERED' && delivered ? (
        <span>
          {de.parcel.deliveredOn}:{' '}
          <span className="font-medium text-slate-800 dark:text-slate-200">
            {dayLabel(delivered)}
          </span>
        </span>
      ) : hasWindow ? (
        <span>
          {de.parcel.expected}:{' '}
          <span className="font-medium text-slate-800 dark:text-slate-200">
            {formatDeliveryWindow(parcel.expected_delivery_from, parcel.expected_delivery_to)}
          </span>
        </span>
      ) : stale == null ? (
        <span>
          {de.parcel.refreshed}: {formatMoment(parcel.refreshed_at)}
        </span>
      ) : null}
      {overdue != null ? (
        <span className="mt-0.5 flex items-center gap-1 justify-end text-rose-700 dark:text-rose-400">
          <AlertTriangle className="h-3 w-3 shrink-0" aria-hidden />
          {short ? de.parcel.overdueShort(overdue) : de.parcel.overdue(overdue)}
        </span>
      ) : (
        // Nothing is coming to move this parcel by itself; the drawer offers the manual
        // delivery mark.
        stale != null && (
          <span
            title={de.parcel.staleHint}
            className="mt-0.5 flex items-center gap-1 justify-end text-rose-700 dark:text-rose-400"
          >
            <AlertTriangle className="h-3 w-3 shrink-0" aria-hidden />
            {de.parcel.staleDays(stale)}
          </span>
        )
      )}
    </div>
  )
}
